"use client";

import { useRef } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight, ExternalLink, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { HeaderSection } from "@/components/shared/header-section";

interface Video {
  id: string;
  title: string;
  link: string;
  thumbnail: string;
  description: string;
  publishedAt: string;
  viewCount?: number;
}

interface Channel {
  id: string;
  name: string;
  channelUrl: string;
  videos: Video[];
}

interface AmbassadorVideosProps {
  channels: Channel[];
}

export default function AmbassadorVideos({ channels }: AmbassadorVideosProps) {
  const scrollRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const scroll = (channelId: string, direction: "left" | "right") => {
    const container = scrollRefs.current[channelId];
    if (!container) return;

    const scrollAmount = 400;
    const newScrollLeft =
      direction === "left"
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: newScrollLeft,
      behavior: "smooth",
    });
  };

  const formatViewCount = (count?: number) => {
    if (!count) return "";
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M views`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K views`;
    return `${count} views`;
  };

  const truncateTitle = (title: string, maxLength: number = 60) => {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength).trim() + "...";
  };

  // Drop videos whose title contains a "$" sign, then filter out channels left empty
  const validChannels = (channels || [])
    .map((channel) => ({
      ...channel,
      videos: (channel.videos || []).filter((video) => !video.title.includes("$")),
    }))
    .filter((channel) => channel.videos.length > 0);

  if (validChannels.length === 0) {
    return (
      <section className="bg-muted/30 py-8">
        <div className="container max-w-7xl">
          <HeaderSection
            label="Featured Content"
            title="Ambassador Videos"
            subtitle="Watch our community ambassadors share their trading strategies and insights using QuantWheel."
          />
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              No featured videos available at the moment. Check back soon!
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-muted/30 py-8">
      <div className="container max-w-7xl">
        <HeaderSection
          label="Featured Content"
          title="Ambassador Videos"
          subtitle="Watch our community ambassadors share their trading strategies and insights using QuantWheel."
        />

        <div className="space-y-12">
          {validChannels.map((channel) => (
            <div key={channel.id} className="space-y-4">
              {/* Channel Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-600">
                    <Youtube className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl">{channel.name}</h3>
                    <a
                      href={channel.channelUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                    >
                      View Channel
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>

                {/* Navigation Buttons */}
                <div className="hidden md:flex gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => scroll(channel.id, "left")}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => scroll(channel.id, "right")}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Videos Carousel */}
              <div className="relative group">
                <div
                  ref={(el) => {
                    scrollRefs.current[channel.id] = el;
                  }}
                  className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
                  style={{
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  }}
                >
                  {channel.videos.map((video) => (
                    <a
                      key={video.id}
                      href={video.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-none w-[300px] group/card"
                    >
                      <Card className="overflow-hidden hover:shadow-lg transition-shadow">
                        {/* Thumbnail */}
                        <div className="relative aspect-video overflow-hidden bg-muted">
                          {video.thumbnail ? (
                            <Image
                              src={video.thumbnail}
                              alt={video.title}
                              fill
                              className="object-cover group-hover/card:scale-105 transition-transform duration-300"
                              unoptimized
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Youtube className="h-12 w-12 text-muted-foreground" />
                            </div>
                          )}
                          {/* Play overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover/card:bg-black/20 transition-colors flex items-center justify-center">
                            <div className="w-16 h-16 rounded-full bg-red-600 opacity-0 group-hover/card:opacity-100 transition-opacity flex items-center justify-center">
                              <div className="w-0 h-0 border-t-8 border-t-transparent border-l-12 border-l-white border-b-8 border-b-transparent ml-1" />
                            </div>
                          </div>
                        </div>

                        {/* Video Info */}
                        <div className="p-4">
                          <h4 className="font-medium text-sm line-clamp-2">
                            {video.title}
                          </h4>
                        </div>
                      </Card>
                    </a>
                  ))}
                </div>

                {/* Gradient overlays for scroll indication */}
                <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent pointer-events-none md:hidden" />
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none md:hidden" />
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-4">
            Want to become a featured ambassador?
          </p>
          <Button asChild>
            <a href="mailto:hello@quantwheel.com">
              Contact Us
            </a>
          </Button>
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
}
